import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { User } from '@vsolv/core/users/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Distributor } from '@wsphere/distributors/domain';
import { Policy, Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject } from 'rxjs';
import { PreviewPDFDialog } from '../../../policy/dialogs';
import { WarrantyWebService } from '../../../warranty/services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-overview-card',
  templateUrl: './warranty-overview-card.component.html',
  standalone: false,
})
export class WarrantyOverviewCardComponent implements OnInit {
  private _warranty: Warranty.Model | null = null;
  public warranty$ = new BehaviorSubject<Warranty.Model | null>(null);

  @Input() set warranty(value: Warranty.Model | null) {
    this._warranty = value;
    this.warranty$.next(value);
    this.newDistributor = value?.distributor ?? null;
    this.initializeDocuments();
  }
  get warranty() {
    return this._warranty;
  }

  selectedDocument: Policy.Document | null = null;
  documents: Policy.Document[] = [];

  @ViewChild('previewPDFDialog') previewPDFDialog?: PreviewPDFDialog;

  emptyUsers: User.Model | null = null;
  @Input() customerId?: string;

  @Input() canUpdate = false;

  @Output() refresh = new EventEmitter<void>();

  newDistributor: Distributor.Model | null = null;
  updatingDistributor = false;

  constructor(
    private warrantySvc: WarrantyWebService,
    private route: ActivatedRoute,
    private toastSvc: ToastService
  ) {}

  contractNumber?: string;
  policyNumber?: string;
  warranties: unknown[] = [];
  isOpen = false;
  hover = false;

  async ngOnInit() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.route.paramMap.subscribe(async (params: { get: (arg0: string) => any }) => {
      const warrantyId = params.get('warrantyId');

      if (warrantyId) {
        const warranty = await this.warrantySvc.getOneById(warrantyId);
        this.warranty = warranty;
      }
    });
  }

  async updateDistributor() {
    if (!this.warranty) return;

    this.updatingDistributor = true;

    try {
      await this.warrantySvc.update(this.warranty.id, { distributorId: this.newDistributor?.id ?? null });

      this.newDistributor = null;
      this.refresh.emit();

      this.toastSvc.show({
        type: 'success',
        title: 'Distributor updated',
        text: "The warranty's distributor has been updated.",
      });
    } catch (err) {
      console.error(err);
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: "An error occured when trying to update the warranty's distributor. Please try again.",
      });
    }

    this.updatingDistributor = false;
  }

  async unlinkDistributor() {
    this.newDistributor = null;
    await this.updateDistributor();
  }

  private initializeDocuments() {
    this.documents = [];
    this.selectedDocument = null;

    if (this.warranty?.policy) {
      if (this.warranty.policy.termsAndConditions) {
        this.documents.push(this.warranty.policy.termsAndConditions);
      }

      if (this.warranty.policy.coverageSummary) {
        this.documents.push(this.warranty.policy.coverageSummary);
      }

      // Auto-select Terms & Conditions by default
      if (this.warranty.policy.termsAndConditions) {
        this.selectedDocument = this.warranty.policy.termsAndConditions;
      } else if (this.warranty.policy.coverageSummary) {
        this.selectedDocument = this.warranty.policy.coverageSummary;
      }
    }
  }

  selectDocument(document: Policy.Document) {
    this.selectedDocument = document;
  }

  getNavboxStyle(id: string) {
    const selected =
      id === this.selectedDocument?.id
        ? 'border-l-4 -ml-1 border-primary-500 rounded-r-none z-10'
        : 'border-1 cursor-pointer';
    return `flex flex-col bg-base pl-4 pr-2 py-2 rounded-lg shadow-md ${selected}`;
  }

  previewDocument(document: Policy.Document) {
    this.selectedDocument = document;
    this.previewPDFDialog?.openDialog();
  }
}
